import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { LookupListService } from "@modules/common/services/lookup-list.service";
import {
  JobDto,
  JobStatusDto,
  JobValidationDto,
  LookupListEx,
  ModelMasterDto,
  ResourceMasterDto,
  fullName,
} from "@shared/models";
import { Observable } from "rxjs";
import { switchMap } from "rxjs/operators";
import { FieldSettings } from "@shared/models/FieldSettings";
import { BaseFormDirective } from "../../common/components";
import {
  CUSTOM_LAYOUT_ID,
  JobService,
  ProductSettingService,
  ResourceService,
  WebLayoutService,
} from "../../common/services";

@Component({
  selector: "abi-validation",
  templateUrl: "./validation.component.html",
  styleUrls: ["./validation.component.scss"],
})
export class JobValidationComponent extends BaseFormDirective implements OnInit {
  @Input() useRouting = true;
  @Input() check: string;
  @Input() jobStatus: JobStatusDto;

  @Input()
  get boundJob(): JobDto {
    return this.job;
  }
  set boundJob(value: JobDto) {
    if (this.job !== value && value) {
      this.displayJob(value);
    }
  }

  disabled = false;
  job: JobDto;
  model: ModelMasterDto;
  resource: ResourceMasterDto;
  submitted = false;
  loaded = false;
  message = "Thank you for your feedback.";
  private jobStatuses: LookupListEx<JobStatusDto>;

  scores = [
    ["btn-outline-danger", "far fa-frown"],
    ["btn-outline-warning", "far fa-meh"],
    ["btn-outline-light-success", "far fa-smile"],
    ["btn-outline-success", "far fa-smile-beam"],
  ];

  confirms = [
    ["btn-outline-success", "fas fa-check", "YES"],
    ["btn-outline-danger", "fas fa-times", "NO"],
  ];

  get score(): FormControl {
    return this.form.get("score") as FormControl;
  }

  get confirmed(): FormControl {
    return this.form.get("confirmed") as FormControl;
  }


  constructor(
    protected layoutService: WebLayoutService,
    private route: ActivatedRoute,
    private jobService: JobService,
    public productSettings: ProductSettingService,
    private lookups: LookupListService,
    private resourceService: ResourceService,
    fb: FormBuilder,
  ) {
    super(layoutService, fb);
    this.createForm();
  }

  fieldSettings: FieldSettings;
  isInited = false;
  ngOnInit() {

    this.lookups.lookupListEx<JobStatusDto>("CodeJobStatus").subscribe((l) => {
      this.jobStatuses = l;
      this.setJobStatus();
    });

    if (this.useRouting) {
      this.route.paramMap
        .pipe(
          switchMap((params: ParamMap) =>
            this.getJob(params.get("jobId"), params.get("check"))
          )
        )
        .subscribe(
          (job) => this.displayJob(job),
          (error) => {
            this.message = error.error_message;
            this.loaded = true;
            this.submitted = true;
            // this.toaster.warning(error.error_message);
            // this.router.navigate(["/"]);
          }
        );
    }


    this.getFieldSettings().subscribe(() => {
      // CODE SMELL: wishful thinking that we can just apply generic logic to all fields
      // Object.keys(this.fieldSettings).forEach((key) => {
      //   if (this.form.get(key)) {
      //     if (this.fieldSettings[key].disabled === false) {
      //       this.form.get(key).enable();
      //     } else {
      //       this.form.get(key).disable();
      //   }
      // });
      if(this.fieldSettings["resourceScore"] && this.fieldSettings["resourceScore"].disabled === false) {
        this.form.get("resourceScore").enable();
      } else {
        this.form.get("resourceScore").disable();
      }
      this.setValidation(true); // requires fieldSettings
      this.isInited = true;
    });
  }

  private setJobStatus() {
    if (this.job && this.jobStatuses) {
      this.jobStatus = this.jobStatuses.item(this.job.statusId);
    }
  }

  get layoutName(): string {
    return "JobClosed";
  }

  private getJob(jobId: string, check: string): Observable<JobDto> {
    this.check = check;
    return this.jobService.getJob(jobId, check);
  }
  label(index: number): string {
    return (
      (this.jobStatus && this.jobStatus.labels[index]) ||
      (index === 0 ? "Yes" : "No")
    );
  }

  confirmation(index: number): string {
    return (
      this.jobStatus.confirmations[index] ||
      "Thank you for submitting your feedback."
    );
  }

  confirmationText(): string {
    return this.confirmed.value === "YES"
      ? this.confirmation(0)
      : this.confirmation(1);
  }

  protected createForm() {
    this.form = this.formBuilder.group({
      score: "",
      resourceScore: "",
      confirmed: "",
    });
    super.createForm();
  }

  setValidation(flag: boolean) {
    if (flag) this.addValidation(this.form);
    else this.removeValidation(this.form);
    // this.form.updateValueAndValidity();
    this.validate(true);
  }

  removeValidation(group: FormGroup) {
    group.get("score").clearValidators();
    group.get("score").updateValueAndValidity();
    group.get("resourceScore").clearValidators();
    group.get("resourceScore").updateValueAndValidity();
    group.get("confirmed").clearValidators();
    group.get("confirmed").updateValueAndValidity();
  }

  // 'validates' property overrides
  addValidation(group: FormGroup) {
    group.get("score").setValidators(this.getValidation("score"));
    group.get("score").updateValueAndValidity();
    group.get("resourceScore").setValidators(this.getValidation("resourceScore"));
    group.get("resourceScore").updateValueAndValidity();
    group.get("confirmed").setValidators(this.getValidation("confirmed"));
    group.get("confirmed").updateValueAndValidity();
  }

  /**
   * Check Fieldsettings for Validates key and apply explicit, and default validation
   */
  getValidation(fieldName: string) {
    if (
      this.fieldSettings &&
      fieldName in this.fieldSettings &&
      this.fieldSettings[fieldName].validates !== undefined
    ) {
      if (this.fieldSettings[fieldName].validates === true) {
        // USE VALIDATION
        switch (fieldName) {
          case "score":
            return Validators.required;
          case "resourceScore":
            return Validators.required;
          case "confirmed":
            return Validators.required;
          default:
            return null;
        }
      } else {
        // EXPLICITY use NO validations
        switch (fieldName) {
          default:
            return null;
        }
      }
    } else {
      // DEFAULTS
      switch (fieldName) {
        case "score":
          return Validators.required;
        case "resourceScore":
          return Validators.required;
        case "confirmed":
          return Validators.required;
        default:
          return null;
      }
    }
  }

  private displayJob(job: JobDto) {
    this.job = job;
    if (this.job.validations && this.job.validations.length) {
      const validations = this.job.validations;
      const jobValidation = validations.filter(v => v.validationId === "JOBRATING" || v.validationId === "");
      // get Job Validation
      if(jobValidation.length) {
        const vali = jobValidation[jobValidation.length - 1];
        if (vali.statusId === this.job.statusId) {
          this.form.patchValue({ confirmed: vali.confirmed, score: vali.score });
          // this.form.disable();
          this.disabled = true;
        }
      }

      const resourceValidation = validations.filter(v => v.validationId === "RESOURCERATING");
      // get Resource validation
      if(resourceValidation.length) {
        const vali = resourceValidation[resourceValidation.length - 1];
        if (vali.statusId === this.job.statusId) {
          this.form.patchValue({ resourceScore: vali.score });
        }
      }
    }

    this.setJobStatus();
    this.loaded = true;
    this.model = job.machine.model;

    if (job.resourceId)
      this.resourceService
        .getResource(job.resourceId)
        .toPromise()
        .then((r) => (this.resource = r));
  }

  saveChanges() {
    const data = this.form.value;
    const vali: JobValidationDto = {
      confirmed: data.confirmed,
      score: String(data.score),
      comment: "",
      statusId: "",
      validationId: "JOBRATING",
    };
    this.loaded = false;
    const changesToSave = [
      this.jobService
      .updateJobValidation(this.job.jobId, this.check, vali)
      .toPromise(),
    ];
    if(this.form.get("resourceScore").enabled) {
      const resValidation: JobValidationDto = {
        confirmed: data.confirmed,
        score: String(data.resourceScore),
        comment: "",
        statusId: "",
        validationId: "RESOURCERATING",
      };
      changesToSave.push(
        this.jobService
          .updateJobValidation(this.job.jobId, this.check, resValidation)
          .toPromise()
      );
    }

    Promise.all(changesToSave).then(() => {
      this.loaded = true;
      this.message = this.confirmationText();
      this.submitted = true;
    });
  }

  // @TODO: migrate to base-form
  showField(fieldName: string, defaultVisibility: boolean = true) {
    if (this.fieldSettings?.[fieldName]?.disabled === undefined) return defaultVisibility; // default
    return this.fieldSettings?.[fieldName]?.disabled === false ? true : false;
  }

  fullName = fullName;
}
